import { supabase } from './lib/supabase';

// Environment configuration
export const config = {
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001',
  spreadJSLicenseKey: import.meta.env.VITE_SPREADJS_LICENSE_KEY || '',
};

// Check if we're in dev bypass mode (no real Supabase configured)
function isDevBypassEnabled(): boolean {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  return !supabaseUrl || supabaseUrl.includes('localhost');
}

// Custom error class for API errors with additional context
export class ApiError extends Error {
  status: number;
  details?: any;

  constructor(message: string, status: number, details?: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

// API helper function with automatic auth token injection
export async function apiRequest(endpoint: string, options?: RequestInit) {
  const url = endpoint.startsWith('http')
    ? endpoint
    : `${config.apiBaseUrl}${endpoint}`;

  // Get the current session token (or use dev bypass)
  let token: string | undefined;

  if (isDevBypassEnabled()) {
    // In dev mode, use a special dev token that backend will recognize
    token = 'dev-bypass-token';
  } else {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    token = session?.access_token;
  }

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ message: 'Unknown error' }));
    const errorMessage =
      errorData.message ||
      errorData.error ||
      `HTTP ${response.status}: ${response.statusText}`;
    throw new ApiError(errorMessage, response.status, errorData);
  }

  return response.json();
}
